import type { ReactNode } from "react";

import { Typography } from "@fielmann-ag/prisma-components";

import { I18nProvider, useI18n } from "@/i18n";

import { Footer } from "./Footer";
import { Header } from "./Header";

interface PageLayoutProps {
  /**
   * Initial locale resolved from Auth0 authorization params, with an optional
   * same-country localStorage override (e.g. Switzerland language preference).
   */
  initialLocale: string;
  /**
   * Login/signup tab bar. Pass together with {@link subtitle} to wrap the form
   * in the shared framing (title → tabs → subtitle → form).
   */
  tabs?: ReactNode;
  /**
   * Screen subtitle. Pass together with {@link tabs} to wrap the form in the
   * shared framing (title → tabs → subtitle → form).
   */
  subtitle?: ReactNode;
  /** Screen form or other main content rendered inside the page shell. */
  children: ReactNode;
}

/**
 * Shared page shell for every auth screen.
 *
 * Renders header, a centered main column, and footer. Content sits directly on
 * the page background — there is no Auth0-style card/widget wrapper.
 *
 * Locale-aware copy comes from {@link I18nProvider}. The locale may change
 * in-session (e.g. Switzerland language switcher) via `useI18n().locale`.
 *
 * Pass both `tabs` and `subtitle` for login/signup screens: that enables the
 * shared chrome (title, tabs, subtitle) around the form. Omit them for screens
 * that only need the page shell (e.g. password-reset).
 */
export function PageLayout({
  initialLocale,
  tabs,
  subtitle,
  children,
}: PageLayoutProps) {
  // True when both slots are set → render title/tabs/subtitle framing around the form.
  const hasAuthScreenChrome = tabs !== undefined && subtitle !== undefined;

  return (
    <I18nProvider locale={initialLocale}>
      <div
        data-theme="light"
        className="flex min-h-screen flex-col bg-background-primary"
      >
        <Header />
        <main className="flex w-full flex-1 flex-col items-center">
          {hasAuthScreenChrome ? (
            <AuthScreenBody tabs={tabs} subtitle={subtitle}>
              {children}
            </AuthScreenBody>
          ) : (
            children
          )}
        </main>
        <Footer />
      </div>
    </I18nProvider>
  );
}

interface AuthScreenBodyProps {
  tabs: ReactNode;
  subtitle: ReactNode;
  children: ReactNode;
}

/**
 * Login/signup content column: the shared framing (title → tabs → subtitle)
 * plus the screen form as `children`.
 *
 * Lives under {@link PageLayout}'s {@link I18nProvider} so `useI18n()` works —
 * a provider cannot serve hooks in the same component that mounts it.
 */
function AuthScreenBody({ tabs, subtitle, children }: AuthScreenBodyProps) {
  const { messages } = useI18n();

  return (
    <div className="flex w-[calc(100%-32px)] max-w-[808px] flex-col gap-1 py-1 sm:w-[calc(100%-48px)] sm:gap-1.5 sm:py-3 md:py-4">
      <Typography
        as="h1"
        variant="title"
        size={400}
        weight="medium"
        color="primary"
        className="text-center !text-[1.375rem] !leading-[1.75rem] sm:!text-[2rem] sm:!leading-[2.4rem]"
      >
        {messages.shared.title}
      </Typography>

      {tabs}

      {subtitle}

      {children}
    </div>
  );
}
